import React, { useState, useEffect, useRef, useContext, forwardRef, useImperativeHandle } from "react";
import apiConfig from "../../config/apiConfig";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchForeignResource } from "../../apis/resources";
import { fetchEnum } from "../../apis/enum";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { useParams } from "react-router-dom";

import { useProgrammepreferenceViewModel } from "../../viewModels/useProgrammepreferenceViewModel";
import { useRaspStore } from "../../store/raspStore";
import { LoginContext } from "../../context/LoginContext";
import CreateGate, { GateFormRef } from "./CreateGate";
import CreateJeemain, { JeeMainApplicationFormRef } from "./CreateJeemain";
import CreateJeeadvanced, { JeeAdvancedApplicationFormRef } from "./CreateJeeadvanced";
import { apiFetch } from "../../apis/apiFetch";
import { useAdmissionDriveKind } from "../../hooks/useAdmissionDriveKind";
import { validate } from "../../utils/validator";
import { programmePreferenceValidationRules } from "../../validators/applicantValidator";
import "./FormStyles.css";
import CreateOlympiadExport, { OlympiadFormRef } from "./CreateOlympiad";

export type resourceMetaData = {
  resource: string;
  fieldValues: any[];
};

// getCookie removed - using apiFetch

export interface ProgrammepreferenceFormRef {
  save: () => Promise<void>;
  getErrors: () => Record<string, string>;
}

const CreateProgrammepreference = forwardRef<ProgrammepreferenceFormRef>((props, ref) => {
  const { user } = useContext(LoginContext);

  const [resMetaData, setResMetaData] = useState<resourceMetaData[]>([]);
  const [showToast, setShowToast] = useState<any>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>(
    {},
  );
  const [selectedSubjects, setSelectedSubjects] = useState<{ [key: string]: string }>({});
  const [masterGateData, setMasterGateData] = useState<Record<string, any>>({});
  // Track number of forms per preference. Key = pref name, Value = count
  const [formCounts, setFormCounts] = useState<Record<string, number>>({});

  const regex = /^(g_|archived|extra_data)/;
  const apiUrl = apiConfig.getResourceUrl("applications");
  const metadataUrl = apiConfig.getResourceMetaDataUrl("applications");

  const fetchedResources = useRef(new Set<string>());
  const fetchedEnum = useRef(new Set<string>());
  const hasPrePopulated = useRef(false);
  const hasUserTouchedRef = useRef(false);
  const queryClient = useQueryClient();

  // Refs for embedded GATE forms
  // Key = `${pref}_${index}`
  const gateRefs = useRef<{ [key: string]: GateFormRef | null }>({});

  const { kind: admissionKind, loading: admissionDriveLoading } = useAdmissionDriveKind();

  // userId and metadata are managed via LoginContext and ViewModel
  const { appId } = useParams<{ appId: string }>();

  const {
    fields,
    setFields,
    enums,
    setEnums,
    foreignKeyData,
    setForeignKeyData,
    dataToSave,
    setDataToSave,
    loadMetadata,
    save,
  } = useProgrammepreferenceViewModel(user?.userId || "", appId || "");

  useEffect(() => {
    loadMetadata();
  }, []);

  // Auto-set user_id helper (optional, similar to GATEDetails if needed)
  useEffect(() => {
    if (!user || !user.email_id) return;
    const users = foreignKeyData["Users"] || [];
    if (!users.length) return;

    const match = users.find(
      (u: any) => {
        const userEmail = (u.email || u.email_id || u.user_email || "").toLowerCase().trim();
        return userEmail === user.email_id.toLowerCase().trim();
      }
    );

    if (match && match.id) {
      setDataToSave((prev: any) => ({
        ...prev,
        user_id: match.id,
      }));
    }
  }, [user, foreignKeyData]);


  // ✅ async function, not useQuery
  const fetchForeignData = async (
    foreignResource: string,
    fieldName: string,
    foreignField: string,
  ) => {
    try {
      const data = await fetchForeignResource(foreignResource);
      setForeignKeyData((prev: any) => ({
        ...prev,
        [foreignResource]: data,
      }));
    } catch (err) {
      console.error(`Error fetching foreign data for ${fieldName}:`, err);
    }
  };

  // ✅ async function, not useQuery
  const fetchEnumData = async (enumName: string) => {
    try {
      const data = await fetchEnum(enumName);
      setEnums((prev: any) => ({
        ...prev,
        [enumName]: data,
      }));
    } catch (err) {
      console.error(`Error fetching enum data for ${enumName}:`, err);
    }
  };

  // ✅ useQuery only here
  const {
    data: metaData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["resMetaData", "programmepreferenceCreate"],
    queryFn: async () => {
      const res = await fetch(metadataUrl, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch metadata: ${res.statusText}`);
      }

      const data = await res.json();

      setResMetaData(data);
      setFields(data[0].fieldValues);

      const foreignFields = data[0].fieldValues.filter(
        (field: any) => field.foreign,
      );
      for (const field of foreignFields) {
        if (!fetchedResources.current.has(field.foreign)) {
          fetchedResources.current.add(field.foreign);

          queryClient.prefetchQuery({
            queryKey: ["foreignData", field.foreign],
            queryFn: () => fetchForeignResource(field.foreign),
          });

          await fetchForeignData(
            field.foreign,
            field.name,
            field.foreign_field,
          );
        }
      }

      const enumFields = data[0].fieldValues.filter(
        (field: any) => field.isEnum === true,
      );
      for (const field of enumFields) {
        if (!fetchedEnum.current.has(field.possible_value)) {
          fetchedEnum.current.add(field.possible_value);

          queryClient.prefetchQuery({
            queryKey: ["enum", field.possible_value],
            queryFn: () => fetchEnum(field.possible_value),
          });

          await fetchEnumData(field.possible_value);
        }
      }

      return data;
    },
  });

  useEffect(() => {
    console.log("data to save", dataToSave);
  }, [dataToSave]);

  const { data: dataRes, isLoading: isLoadingDataRes, error: errorDataRes } = useQuery({
    queryKey: ['resourceData', 'programmepreferenceCreate', user?.userId, localStorage.getItem("activeDriveId")],
    queryFn: async () => {
      if (!user?.userId) return [];
      const response = await apiFetch(
        `${apiConfig.getResourceUrl('applications')}?queryId=GET_APPLICATIONS_BY_USER_ID&args=user_id:${user.userId}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        throw new Error("Error: " + response.status);
      }

      const data = await response.json();
      return data.resource || [];
    },
    enabled: !!user?.userId
  });

  useEffect(() => {
    if (dataRes) {
      let dataToStore = { resource: "programmepreference", records: dataRes };
      useRaspStore.getState().initializeStore(user?.userId || "", dataToStore);
    }
  }, [dataRes, user]);

  // Pre-populate form with existing data when user_id is resolved and records are loaded
  useEffect(() => {
    const activeDriveId = localStorage.getItem("activeDriveId");
    if (!hasPrePopulated.current && dataRes && dataRes.length > 0 && dataToSave.user_id && activeDriveId) {
      // If user already interacted (picked checkboxes), don't overwrite their choices
      // with the async pre-populated record.
      if (hasUserTouchedRef.current) return;

      const userRecord = dataRes.find((r: any) => r.user_id === dataToSave.user_id && r.drive_id === activeDriveId);
      if (userRecord) {
        hasPrePopulated.current = true;
        // Merge userRecord into dataToSave, ensuring id and drive_id are preserved
        setDataToSave((prev: any) => ({ ...userRecord, ...prev }));

        // 🔥 Initialize masterMap with existing program-specific data
        const initialMaster: Record<string, any> = {};
        const prefFields = ["rollno", "score", "rank", "scorecard", "subjects", "year"];
        for (let i = 1; i <= 5; i++) {
          const pName = userRecord[`pref${i}`];
          if (pName) {
            initialMaster[pName] = { name: pName };
            prefFields.forEach((f) => {
              initialMaster[pName][f] = userRecord[`pref${i}${f}`];
            });
          }
        }
        setMasterGateData(initialMaster);
      }
    }
  }, [dataRes, dataToSave.user_id]);

  const handleCreate = async () => {

    const extraData: Record<string, any> = {};


    const vErrors: Record<string, string> = {};

    // 🔥 Custom group validation
    const isNonEmpty = (v: unknown) => {
      if (v === null || v === undefined) return false;
      if (typeof v === "string") return v.trim() !== "";
      return String(v).trim() !== "";
    };

    // if (!isNonEmpty(dataToSave.pref1) && !isNonEmpty(dataToSave.pref2) && !isNonEmpty(dataToSave.pref3)) {
    //   vErrors["pref1"] = "At least one programme must be selected";
    // }

    // 🔁 Merge with existing validation
    const fieldErrors = validate(dataToSave, programmePreferenceValidationRules);
    console.log("fieldErrors", fieldErrors)
    Object.assign(vErrors, fieldErrors);
    
    let hasGateErrors = false;

    const isBtechFlow = admissionKind === "btech";

    if (isBtechFlow) {
      // 1. First preference is mandatory
      if (!isNonEmpty(dataToSave.pref1)) {
        vErrors["pref1"] = "First preference is required";
      }

    }

    if (isBtechFlow) {
      for (let i = 1; i <= 5; i++) {
        for (const f of ["rollno", "score", "rank", "scorecard", "subjects", "year"]) {
          extraData[`pref${i}${f}`] = null;
        }
      }

    } else {
      const mapGateData = (gateData: any, prefix: string) => {
        if (!gateData) return;
        extraData[`${prefix}rollno`] = gateData.rollno;
        extraData[`${prefix}score`] = gateData.score ? parseInt(gateData.score, 10) : null;
        extraData[`${prefix}rank`] = gateData.rank;
        extraData[`${prefix}scorecard`] = gateData.scorecard;
        extraData[`${prefix}subjects`] = gateData.subjects;
        extraData[`${prefix}year`] = gateData.year;
      };

      const ref1 = gateRefs.current["pref1_0"];
      const ref2 = gateRefs.current["pref2_0"];
      const ref3 = gateRefs.current["pref3_0"];

      if (dataToSave["pref1"] && ref1) {
        const gErrs = ref1.validate();
        if (Object.keys(gErrs).length > 0) hasGateErrors = true;
        mapGateData(ref1.getData(), "pref1");
      }

      if (dataToSave["pref2"] && ref2) {
        const gErrs = ref2.validate();
        if (Object.keys(gErrs).length > 0) hasGateErrors = true;
        mapGateData(ref2.getData(), "pref2");
      }

      if (dataToSave["pref3"] && ref3) {
        const gErrs = ref3.validate();
        if (Object.keys(gErrs).length > 0) hasGateErrors = true;
        mapGateData(ref3.getData(), "pref3");
      }

      if (hasGateErrors) {
        vErrors["gate"] = "Please fill all required GATE fields.";
      }
    }
    setErrors(vErrors);

    if (Object.keys(vErrors).length > 0) return;
    console.log("extraData", extraData)
    await save(extraData);

    setShowToast(true);
    queryClient.invalidateQueries({ queryKey: ["resourceData", "programmepreferenceCreate"] });
  };

  useImperativeHandle(ref, () => ({
    save: handleCreate,
    getErrors: () => errors
  }));



  const programOptions = enums["Programme"] || [];
  const isNonEmpty = (v: unknown) => {
    if (v === null || v === undefined) return false;
    if (typeof v === "string") return v.trim() !== "";
    return String(v).trim() !== "";
  };

  const isValidSelection = (val: string) =>
    val && val !== "None";

  const cleanedBtechProgramOptions = programOptions
    .filter(isNonEmpty)
    .filter((val: string) => {
      const lower = val.toLowerCase();
      return lower.includes("b.tech") || lower.includes("integrated");
    });

  const cleanedMtechProgramOptions = programOptions
    .filter(isNonEmpty)
    .filter((val: string) => {
      const lower = val.toLowerCase();
      return !lower.includes("b.tech") && !lower.includes("integrated");
    });

  if (admissionDriveLoading) {
    return (
      <div className="form-container">
        <p className="text-muted">Loading…</p>
      </div>
    );
  }

  return (
    <>

      {admissionKind === "mtech" && <div className="form-container">

        {(
          <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#e0f2fe', borderRadius: '6px', border: '1px solid #bae6fd', fontSize: '14px' }}>
            <>
              <strong style={{ color: '#0369a1' }}>Selection procedure for following Programme: </strong>
              <a href="https://www.iiitb.ac.in/courses/mtech-cse-admissions#title-tab13024" target="_blank" rel="noopener noreferrer" style={{ color: '#0284c7', textDecoration: 'underline', marginLeft: '8px', fontWeight: '500' }}>M.Tech CSE</a>
              <span style={{ margin: '0 10px', color: '#7dd3fc' }}>|</span>
              <a href="https://www.iiitb.ac.in/courses/mtech-ece-admissions#title-tab13002" target="_blank" rel="noopener noreferrer" style={{ color: '#0284c7', textDecoration: 'underline', fontWeight: '500' }}>M.Tech ECE</a>
              <span style={{ margin: '0 10px', color: '#7dd3fc' }}>|</span>
              <a href="https://www.iiitb.ac.in/courses/mtech-ai-ds-admissions#title-tab13014" target="_blank" rel="noopener noreferrer" style={{ color: '#0284c7', textDecoration: 'underline', fontWeight: '500' }}>M.Tech AI&DS</a>
              <div style={{ marginTop: '6px' }}>
                <strong style={{ color: '#0369a1' }}>Note: Please click on the &quot;Selection Procedure&quot; tab on the page that opens.</strong>
              </div>
            </>
          </div>
        )}

        {<div className="form-grid">
          <div className="form-group" style={{ gridColumn: "1 / -1" }}>
            <label className="form-subtitle">Select Programmes you want to apply for (Min 1, Max 3) *</label>
            <div className="checkbox-group" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: "10px" }}>
              {cleanedMtechProgramOptions.map((val: any, idx: any) => {
                const selectedValues = [dataToSave["pref1"], dataToSave["pref2"], dataToSave["pref3"]].filter(
                  isNonEmpty,
                );

                const isChecked = selectedValues.includes(val);
                const isDisabled = !isChecked && selectedValues.length >= 3;

                return (
                  <div key={idx} className="form-check" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`prog-${idx}`}
                      value={val}
                      checked={isChecked}
                      disabled={isDisabled}
                      onChange={(e) => {
                        hasUserTouchedRef.current = true;
                        const prefFields = ["", "rollno", "score", "rank", "scorecard", "subjects", "year"];

                        // 1. Capture current live data from active forms into masterGateData
                        const currentLiveMap = { ...masterGateData };
                        for (let i = 1; i <= 3; i++) {
                          const pName = dataToSave[`pref${i}`];
                          const ref = gateRefs.current[`pref${i}_0`]; // Key is prefN_0
                          if (pName && ref) {
                            const live = ref.getData();
                            currentLiveMap[pName] = { ...currentLiveMap[pName], ...live, name: pName };
                          }
                        }
                        setMasterGateData(currentLiveMap);

                        // 2. Identify new selection list
                        let currentSelections = [dataToSave.pref1, dataToSave.pref2, dataToSave.pref3].filter(isNonEmpty);
                        let newSelections: string[] = [];

                        if (e.target.checked) {
                          if (!currentSelections.includes(val) && currentSelections.length < 3) {
                            newSelections = [...currentSelections, val];
                          } else {
                            newSelections = [...currentSelections];
                          }
                        } else {
                          newSelections = currentSelections.filter(v => v !== val);
                        }

                        // 3. Prepare updatedData
                        const updatedData = { ...dataToSave };

                        // Clear all slots first
                        for (let i = 1; i <= 3; i++) {
                          prefFields.forEach(f => {
                            updatedData[f ? `pref${i}${f}` : `pref${i}`] = null;
                          });
                        }

                        // 4. Map the new ordered selections back into slots pref1, 2, 3
                        // pulling data from our persistent masterGateData
                        newSelections.forEach((selName, idx) => {
                          const i = idx + 1;
                          updatedData[`pref${i}`] = selName;

                          const savedData = currentLiveMap[selName];
                          if (savedData) {
                            prefFields.forEach(f => {
                              if (f) updatedData[`pref${i}${f}`] = savedData[f];
                            });
                          }
                        });

                        setDataToSave(updatedData);
                      }}
                      style={{ width: "18px", height: "18px", cursor: isDisabled ? "not-allowed" : "pointer" }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`prog-${idx}`}
                      style={{ cursor: isDisabled ? "not-allowed" : "pointer", color: isDisabled ? "#999" : "inherit" }}
                    >
                      {val}
                    </label>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: "10px", fontSize: "0.9em", color: "#666" }}>
              <div style={{ fontWeight: 600, marginBottom: "4px" }}>Selected Programme:</div>
              {dataToSave["pref1"] && <div>{dataToSave["pref1"]}</div>}
              {dataToSave["pref2"] && <div>{dataToSave["pref2"]}</div>}
              {dataToSave["pref3"] && <div>{dataToSave["pref3"]}</div>}
            </div>
            {errors.pref1 && <span className="error-text" style={{ display: 'block', marginTop: '10px' }}>{errors.pref1}</span>}
          </div>

        </div>}

        {(
          <div className="gate-forms-section" style={{ marginTop: "30px" }}>
            {[
              { key: "pref1", value: dataToSave["pref1"] },
              { key: "pref2", value: dataToSave["pref2"] },
              { key: "pref3", value: dataToSave["pref3"] },
            ].map((prefObj) => {
              if (!prefObj.value) return null;

              const pref = prefObj.value;
              const prefix = prefObj.key;
              const uniqueKey = `${prefix}_0`;

              let allowedSubjects: string[] | undefined = undefined;

              if (pref === "MTech_CSE") {
                allowedSubjects = ["Computer Science and Information Technology"];
              } else if (pref === "MTech_ECE") {
                allowedSubjects = [
                  "Electronics and Communication Engineering",
                  "Electrical Engineering",
                ];
              } else if (pref === "MTech_AI&DS") {
                allowedSubjects = ["Data Science and Artificial Intelligence"];
              }

              const excludedSubjects = Object.entries(selectedSubjects)
                .filter(([key, val]) => key !== uniqueKey && val)
                .map(([_, val]) => val);

              return (
                <div key={pref} style={{ marginTop: "20px", borderTop: "1px solid #eee", paddingTop: "20px" }}>
                  <h5>GATE Exam Details for {pref.split("_").pop()}</h5>

                  <CreateGate
                    ref={(el) => (gateRefs.current[uniqueKey] = el)}
                    prefix={prefix}
                    allowedSubjects={allowedSubjects}
                    excludedSubjects={excludedSubjects}
                    onSubjectChange={(subj) => {
                      setSelectedSubjects((prev) => ({
                        ...prev,
                        [uniqueKey]: subj,
                      }));
                    }}
                    preSelectedSubject={allowedSubjects ? allowedSubjects[0] : undefined}
                    initialData={dataToSave}
                  />
                </div>
              );
            })}
          </div>
        )}

      </div>}
      {admissionKind === "btech" && (
        <>
          <div className="form-container">
            <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#e0f2fe', borderRadius: '6px', border: '1px solid #bae6fd', fontSize: '14px' }}>
              <strong style={{ color: '#0369a1' }}>
                Kindly go through all the tabs on the B.Tech/Integrated M.Tech programme details page before filling in your programme preferences.
              </strong>
              <div style={{ marginTop: '8px' }}>
                <a href="https://www.iiitb.ac.in/courses/btech-integrated-mtech" target="_blank" rel="noopener noreferrer" style={{ color: '#0284c7', textDecoration: 'underline', fontWeight: '500' }}>
                  B.Tech & Integrated M.Tech
                </a>
              </div>
            </div>
            <div className="form-subtitle-row" style={{ justifyContent: "space-between" }}>
              <h3 className="form-subtitle" style={{ marginBottom: 0 }}>Programme Preference</h3>
              <button
                type="button"
                className="upload-btn"
                style={{
                  color: "#ffffff",
                  border: "1px solid",
                  padding: "6px 12px",
                  fontSize: "13px"
                }}
                onClick={() => {
                  const updatedData = { ...dataToSave };
                  for (let i = 1; i <= 5; i++) {
                    const f = `pref${i}`;
                    const prefFields = ["", "rollno", "score", "rank", "scorecard", "subjects", "year"];
                    prefFields.forEach((pf) => {
                      updatedData[pf ? `${f}${pf}` : f] = null;
                    });
                  }
                  setDataToSave(updatedData);
                }}
              >
                Reset Preferences
              </button>
            </div>

            <div className="form-grid">
              {[1, 2, 3, 4, 5].map((num) => {
                const field = `pref${num}`;

                // Get already selected values except current dropdown
                const selectedValues = [1, 2, 3, 4, 5]
                  .map((i) => dataToSave[`pref${i}`])
                  .filter((v, idx) => isValidSelection(v) && idx !== num - 1);

                // Filter options to exclude already selected
                const availableOptions = cleanedBtechProgramOptions.filter(
                  (opt: string) => !selectedValues.includes(opt)
                );

                return (
                  <div key={num} className="form-group">
                    <label className="form-subtitle">
                      Preference {num} {num === 1 && "*"}
                    </label>

                    <select
                      className="form-control"
                      value={dataToSave[field] || ""}
                      disabled={num !== 1 && !isValidSelection(dataToSave[`pref${num - 1}`])}
                      onChange={(e) => {
                        const value = e.target.value;

                        const updatedData = { ...dataToSave };

                        // Clear current and all next prefs when changed
                        for (let i = num; i <= 5; i++) {
                          const f = `pref${i}`;
                          const prefFields = ["", "rollno", "score", "rank", "scorecard", "subjects", "year"];

                          prefFields.forEach((pf) => {
                            updatedData[pf ? `${f}${pf}` : f] = null;
                          });
                        }

                        // Set selected value
                        updatedData[field] = value;

                        setDataToSave(updatedData);
                      }}
                    >
                      <option value="">Select Programme</option>

                      {/* Add "None" option from pref2 onwards */}
                      {num !== 1 && <option value="None">None</option>}

                      {availableOptions.map((opt: string, idx: number) => (
                        <option key={idx} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                    {errors[field] && (
                      <span className="error-text" style={{ bottom: "-15px" }}>
                        {errors[field]}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
});

export default CreateProgrammepreference;
